/**
 * Helpers for the Easy Quote guest form (`EasyQuoteGuestForm.tsx`): field validation and the
 * `axiosPOST` call wrapper. `LoginHelpers/LoginForm.tsx` also uses `scrollToMessage`. `axiosGET`
 * has no importer.
 *
 * `axiosPOST` / `axiosGET` are *Legacy: don't copy* (web-architecture ADR-0043 §2): a new call
 * used in one place is written inline with `useApplication().axiosInstance`, one used in several
 * places is a typed function in `src/services/`, and either takes its URL and types from
 * `@ryerson/common.service-contracts`. A second, drifted copy of `axiosPOST` and
 * `scrollToMessage` (not `axiosGET`) is in
 * `components/ContactFormWithFileAttacher/ContactFormWithFile.Functions.tsx`.
 */
import React from "react";
import { getCurrentPattern, Language, validateZipCode } from "@ryerson/frontend.common";
import { AxiosError, AxiosInstance, AxiosResponse, isAxiosError } from "axios";

/**
 * Helper Function to validate the customer number
 * @param value - The customer number value to validate
 * @returns A boolean indicating if the customer number is valid
 */
export const validateCustomerNumber = (value: string): boolean => {
	return !!value?.trim();
};

/**
 * Helper Function to Validate the zip code based on country-specific regex patterns: true for a
 * non-blank value that matches the US ZIP or the Canadian postal-code pattern
 * (`@ryerson/frontend.common` → `getCurrentPattern` / `validateZipCode`). The messages those
 * return are discarded, so English is passed for both.
 * @param value - The zip code value to validate
 * @returns A boolean indicating if the zip code is valid
 */
export const validatePostalCode = (value: string): boolean => {
	let valid = !!value?.trim();

	if (valid) {
		let currentPattern = getCurrentPattern(value, Language.en);
		let err = validateZipCode(value, currentPattern, Language.en);
		valid = !(err.length > 0);
	}

	return valid;
};

/**
 * Check if all form inputs are valid before submission
 * @param customerNumber - Customer number value
 * @param zipCode - Zip code value
 * @returns A boolean indicating if all inputs are valid
 */
export const checkAllInputsValid = (customerNumber: string, zipCode: string): boolean => {
	const customerNumberValid = validateCustomerNumber(customerNumber);
	const zipCodeValid = validatePostalCode(zipCode);

	return customerNumberValid && zipCodeValid;
};

/**
 * Interface for consistent error payload structure
 */
interface ErrorPayload {
	data?: any;
	status?: number;
	statusText?: string;
}

/**
 * Interface for Axios POST requests
 * Wrapper function for Axios Request
 */
interface AxiosPost<T_REQ, T_RESPONSE> {
	apiUrl: string;
	axiosInstance: AxiosInstance;
	requestBody: T_REQ;
	unMounted: React.MutableRefObject<boolean>; // ref to track if component is unmounted
	successCB?: (data: T_RESPONSE) => void;
	errorCB?: (error: ErrorPayload) => void;
	isFetching?: [boolean, React.Dispatch<React.SetStateAction<boolean>>]; // UseState[val,setter] to track fetching state
}

/**
 * Posts `requestBody` to `apiUrl` and reports the outcome through callbacks: `successCB` with
 * the body for a 200, otherwise `errorCB` with `{ data, status, statusText }`, where `data` is
 * the error response body. Sets `isFetching` true before the call and false when it settles.
 * Every state update and callback is skipped while `unMounted.current` is true. It doesn't
 * return the request's promise, so the caller can't await it.
 */
export const axiosPOST = <T_REQ, T_RESPONSE>({
	apiUrl,
	axiosInstance,
	requestBody = {} as T_REQ,
	unMounted,
	successCB,
	errorCB,
	isFetching,
}: AxiosPost<T_REQ, T_RESPONSE>) => {
	const [, setIsFetching] = isFetching || [];

	if (!unMounted.current) {
		setIsFetching && setIsFetching(true);
	}

	axiosInstance
		.post<T_REQ, AxiosResponse<T_RESPONSE>>(apiUrl, requestBody)
		.then((response: AxiosResponse<T_RESPONSE> | AxiosError) => {
			if (!unMounted.current) {
				setIsFetching && setIsFetching(false);

				// Check if response is actually an error using axios's built-in type guard
				// `@ryerson/frontend.application`'s interceptors reject an error status, so it
				// reaches `.catch` below. What arrives here as an error is a cancelled request
				// (resolved as `{ requestCancelled: true }`) or a 2xx other than 200. A 401 outside
				// sign-in resolves `undefined` after the interceptor redirects; reading `.status`
				// on it throws, which also lands in `.catch`.
				const isAxiosErrorResponse = isAxiosError(response);
				const isError = isAxiosErrorResponse || response.status !== 200;

				if (isError) {
					// Extract error data from standard AxiosError structure or fallback to response.data
					const errorData = isAxiosErrorResponse
						? response.response?.data
						: (response as AxiosResponse).data;
					// Extract specific properties to create clean, consistent error payload
					const errorPayload: ErrorPayload = {
						data: errorData,
						status: response.status,
						statusText: isAxiosErrorResponse
							? response.message
							: (response as AxiosResponse).statusText,
					};
					errorCB && errorCB(errorPayload);
				} else {
					successCB && successCB((response as AxiosResponse<T_RESPONSE>).data);
				}
			}
		})
		.catch((error: AxiosError) => {
			if (!unMounted.current) {
				setIsFetching && setIsFetching(false);

				// Extract specific properties to create clean, consistent error payload
				const errorPayload: ErrorPayload = {
					data: error.response?.data,
					status: error.response?.status,
					statusText: error.response?.statusText || error.message,
				};
				errorCB && errorCB(errorPayload);
			}
		});
};

/**
 * Interface for Axios GET requests
 * Wrapper function for Axios Request
 */
interface AxiosGet<T_RESPONSE> {
	apiUrl: string;
	axiosInstance: AxiosInstance;
	unMounted: React.MutableRefObject<boolean>; // ref to track if component is unmounted
	successCB?: (data: T_RESPONSE) => void;
	errorCB?: (error: ErrorPayload) => void;
	isFetching?: [boolean, React.Dispatch<React.SetStateAction<boolean>>]; // UseState[val,setter] to track fetching state
}

/** The GET twin of {@link axiosPOST}, with the same callbacks. No file imports it. */
export const axiosGET = <T_RESPONSE,>({
	apiUrl,
	axiosInstance,
	unMounted,
	successCB,
	errorCB,
	isFetching,
}: AxiosGet<T_RESPONSE>) => {
	const [, setIsFetching] = isFetching || [];

	if (!unMounted.current) {
		setIsFetching && setIsFetching(true);
	}

	axiosInstance
		.get<T_RESPONSE, AxiosResponse<T_RESPONSE>>(apiUrl)
		.then((response: AxiosResponse<T_RESPONSE> | AxiosError) => {
			if (!unMounted.current) {
				setIsFetching && setIsFetching(false);

				// Check if response is actually an error using axios's built-in type guard
				// Error statuses reach `.catch`; see the same check in `axiosPOST`.
				const isAxiosErrorResponse = isAxiosError(response);
				const isError = isAxiosErrorResponse || response.status !== 200;

				if (isError) {
					// Extract error data from standard AxiosError structure or fallback to response.data
					const errorData = isAxiosErrorResponse
						? response.response?.data
						: (response as AxiosResponse).data;
					// Extract specific properties to create clean, consistent error payload
					const errorPayload: ErrorPayload = {
						data: errorData,
						status: response.status,
						statusText: isAxiosErrorResponse
							? response.message
							: (response as AxiosResponse).statusText,
					};
					errorCB && errorCB(errorPayload);
				} else {
					successCB && successCB((response as AxiosResponse<T_RESPONSE>).data);
				}
			}
		})
		.catch((error: AxiosError) => {
			if (!unMounted.current) {
				setIsFetching && setIsFetching(false);

				// Extract specific properties to create clean, consistent error payload
				const errorPayload: ErrorPayload = {
					data: error.response?.data,
					status: error.response?.status,
					statusText: error.response?.statusText || error.message,
				};
				errorCB && errorCB(errorPayload);
			}
		});
};

/**
 * Scroll to a specific message container
 * @param containerID - The ID (preferred) or class name of the container to scroll to
 * @param timeOutLength - The timeout length before scrolling
 * @param scrollDuration - Scroll animation duration in ms (higher = slower)
 */
export function scrollToMessage(
	containerID: string,
	timeOutLength: number = 200,
	scrollDuration: number = 1200
): void {
	function getOffset(el: HTMLElement) {
		const rect = el.getBoundingClientRect();
		return {
			left: rect.left + window.scrollX,
			top: rect.top + window.scrollY,
		};
	}

	function animateScrollTo(targetTop: number, duration: number) {
		const startTop = window.scrollY;
		const distance = targetTop - startTop;
		const startTime = performance.now();

		const easeInOutQuad = (t: number) =>
			t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

		const step = (currentTime: number) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const eased = easeInOutQuad(progress);

			window.scrollTo({
				top: startTop + distance * eased,
				left: 0,
			});

			if (progress < 1) {
				requestAnimationFrame(step);
			}
		};

		requestAnimationFrame(step);
	}

	// Time out to allow screen to adjust before scrolling
	setTimeout(() => {
		const scrollTarget =
			document?.getElementById(containerID) || document?.querySelector(`.${containerID}`);

		const additionalOffset = 150;
		if (scrollTarget) {
			const offset = getOffset(scrollTarget as HTMLElement).top - additionalOffset;
			animateScrollTo(offset, scrollDuration);
		}
	}, timeOutLength);
}
